// src/pages/stock/NouvelInventaire.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Table, Row, Col, Form, Button, Spinner, Alert,
    Badge, Pagination
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

export default function NouvelInventaire({ onInventaireCreated }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [produits, setProduits] = useState([]);
    const [filteredProduits, setFilteredProduits] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [filterEmplacement, setFilterEmplacement] = useState('');
    const [filterCategorie, setFilterCategorie] = useState('');
    const [categories, setCategories] = useState([]);
    const [emplacements, setEmplacements] = useState([]);
    const [inventaire, setInventaire] = useState({
        date_inventaire: new Date().toISOString().split('T')[0],
        commentaire: '',
        lignes: []
    });
    const [errors, setErrors] = useState({});
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchProduits();
        fetchCategories();
    }, []);

    useEffect(() => {
        filterProduits();
    }, [searchText, filterEmplacement, filterCategorie, produits]);

    const fetchProduits = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/inventaires.php?action=produits`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setProduits(result.data || []);
                setCurrentPage(1);
                const uniqEmplacements = [...new Set(result.data.map(p => p.emplacement).filter(Boolean))];
                setEmplacements(uniqEmplacements);
            }
        } catch (err) {
            console.error("Erreur chargement produits", err);
        }
    };

    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/categories.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const result = await response.json();
                setCategories(result.data || []);
            }
        } catch (err) {
            console.error("Erreur catégories", err);
        }
    };

    const filterProduits = () => {
        let filtered = produits;
        if (searchText) {
            filtered = filtered.filter(p =>
                p.reference?.toLowerCase().includes(searchText.toLowerCase()) ||
                p.designation?.toLowerCase().includes(searchText.toLowerCase())
            );
        }
        if (filterCategorie) {
            filtered = filtered.filter(p => p.id_categorie == filterCategorie);
        }
        if (filterEmplacement) {
            filtered = filtered.filter(p => p.emplacement === filterEmplacement);
        }
        setFilteredProduits(filtered);
        setCurrentPage(1);
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredProduits.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredProduits.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
            if (startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item key={number} active={number === currentPage} onClick={() => handlePageChange(number)}>
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            items.push(<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>);
        }

        return items;
    };

    const handleQuantiteChange = (produitId, value) => {
        const quantite = parseInt(value) || 0;
        setInventaire(prev => {
            const existingIndex = prev.lignes.findIndex(l => l.id_produit === produitId);
            const produit = produits.find(p => p.id_produit === produitId);
            let nouvellesLignes = [...prev.lignes];
            if (existingIndex >= 0) {
                if (quantite > 0) {
                    nouvellesLignes[existingIndex] = {
                        id_produit: produitId,
                        quantite_reelle: quantite
                    };
                } else {
                    nouvellesLignes.splice(existingIndex, 1);
                }
            } else if (quantite > 0) {
                nouvellesLignes.push({
                    id_produit: produitId,
                    quantite_reelle: quantite
                });
            }
            return { ...prev, lignes: nouvellesLignes };
        });
    };

    const getQuantiteSaisie = (produitId) => {
        const ligne = inventaire.lignes.find(l => l.id_produit === produitId);
        return ligne ? ligne.quantite_reelle : '';
    };

    const calculerResume = () => {
        const totalTheorique = inventaire.lignes.reduce((sum, l) => {
            const produit = produits.find(p => p.id_produit === l.id_produit);
            return sum + (produit?.stock_actuel || 0);
        }, 0);
        const totalReel = inventaire.lignes.reduce((sum, l) => sum + (l.quantite_reelle || 0), 0);
        const totalEcart = totalReel - totalTheorique;
        const nbProduits = inventaire.lignes.length;
        return { totalTheorique, totalReel, totalEcart, nbProduits };
    };

    const validateForm = () => {
        const newErrors = {};
        if (!inventaire.date_inventaire) newErrors.date = "La date est requise";
        if (inventaire.lignes.length === 0) newErrors.lignes = "Saisissez au moins un produit";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        setLoading(true);
        setSubmitError('');
        try {
            const token = localStorage.getItem("token");
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;
            const payload = {
                action: 'create',
                date_inventaire: inventaire.date_inventaire,
                commentaire: inventaire.commentaire,
                lignes: inventaire.lignes.map(l => ({
                    id_produit: l.id_produit,
                    quantite_reelle: l.quantite_reelle
                })),
                id_utilisateur: user?.id || user?.id_utilisateur || 1
            };
            const response = await fetch(`${API_BASE_URL}/inventaires.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            const result = await response.json();
            if (result.success) {
                setSubmitSuccess(true);
                if (onInventaireCreated) {
                    onInventaireCreated();
                }
                setTimeout(() => {
                    navigate('/stock/inventaire/cours');
                }, 1500);
            } else {
                setSubmitError(result.message || "Erreur lors de la création");
            }
        } catch (err) {
            console.error("Erreur:", err);
            setSubmitError("Erreur de connexion au serveur");
        } finally {
            setLoading(false);
        }
    };

    const resume = calculerResume();

    return (
        <>
            <Form onSubmit={handleSubmit}>
                {/* Barre d'outils */}
                <Row className="g-3 mb-4">
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label className="fw-semibold">Date inventaire</Form.Label>
                            <Form.Control
                                type="date"
                                value={inventaire.date_inventaire}
                                onChange={(e) => setInventaire({...inventaire, date_inventaire: e.target.value})}
                                isInvalid={!!errors.date}
                                className="border-2 rounded-3"
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.date}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label className="fw-semibold">Commentaire</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Observations..."
                                value={inventaire.commentaire}
                                onChange={(e) => setInventaire({...inventaire, commentaire: e.target.value})}
                                className="border-2 rounded-3"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3} className="d-flex align-items-end">
                        <Button
                            type="submit"
                            variant="primary"
                            className="w-100 py-2 rounded-pill"
                            disabled={loading}
                            style={{ background: COLORS.primary, border: 'none' }}
                        >
                            {loading ? <Spinner size="sm" /> : 'Créer l\'inventaire'}
                        </Button>
                    </Col>
                </Row>

                {errors.lignes && (
                    <Alert variant="warning" className="mb-4 rounded-3 border-0 shadow-sm">
                        <i className="ri-alert-line me-2"></i>
                        {errors.lignes}
                    </Alert>
                )}

                {/* Résumé en cours */}
                {inventaire.lignes.length > 0 && (
                    <Card className="border-0 shadow-sm rounded-3 mb-4" style={{ background: '#f8fafc' }}>
                        <Card.Body className="p-3">
                            <Row className="g-3">
                                <Col md={3}>
                                    <small className="text-muted d-block">Produits saisis</small>
                                    <h4 className="mb-0">{resume.nbProduits}</h4>
                                </Col>
                                <Col md={3}>
                                    <small className="text-muted d-block">Qté théorique</small>
                                    <h4 className="mb-0">{resume.totalTheorique}</h4>
                                </Col>
                                <Col md={3}>
                                    <small className="text-muted d-block">Qté réelle</small>
                                    <h4 className="mb-0">{resume.totalReel}</h4>
                                </Col>
                                <Col md={3}>
                                    <small className="text-muted d-block">Écart</small>
                                    <h4 className={`mb-0 ${resume.totalEcart > 0 ? 'text-success' : resume.totalEcart < 0 ? 'text-danger' : ''}`}>
                                        {resume.totalEcart > 0 ? '+' : ''}{resume.totalEcart}
                                    </h4>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                )}

                {/* Filtres avec fond primaryDark */}
                <Card className="border-0 shadow-sm rounded-3 mb-4" style={{ background: COLORS.primaryDark }}>
                    <Card.Body className="py-3 px-4">
                        <Row className="g-3">
                            <Col md={5}>
                                <div className="position-relative">
                                    <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-white-50"></i>
                                    <Form.Control
                                        type="text"
                                        placeholder="Rechercher un produit..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        className="ps-5 rounded-pill"
                                        style={{ background: 'rgba(255,255,255,0.9)' }}
                                    />
                                </div>
                            </Col>
                            <Col md={3}>
                                <Form.Select
                                    value={filterCategorie}
                                    onChange={(e) => setFilterCategorie(e.target.value)}
                                    className="rounded-pill"
                                    style={{ background: 'rgba(255,255,255,0.9)' }}
                                >
                                    <option value="">Toutes catégories</option>
                                    {categories.map(c => (
                                        <option key={c.id_categorie} value={c.id_categorie}>
                                            {c.nom_categorie}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Select
                                    value={filterEmplacement}
                                    onChange={(e) => setFilterEmplacement(e.target.value)}
                                    className="rounded-pill"
                                    style={{ background: 'rgba(255,255,255,0.9)' }}
                                >
                                    <option value="">Tous emplacements</option>
                                    {emplacements.map(e => (
                                        <option key={e} value={e}>{e}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={1}>
                                <Button
                                    variant="light"
                                    onClick={() => {
                                        setSearchText('');
                                        setFilterCategorie('');
                                        setFilterEmplacement('');
                                    }}
                                    className="rounded-pill w-100"
                                    style={{ color: COLORS.primaryDark }}
                                >
                                    <i className="ri-refresh-line"></i>
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Tableau de saisie avec header #21ACCD */}
                <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                    <Card.Header className="border-0 py-3" style={{ background: COLORS.secondary }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                                <i className="ri-stack-line text-white me-2 fs-5"></i>
                                <span className="fw-semibold text-white">Saisie des produits</span>
                                <Badge bg="light" text="dark" className="ms-2 rounded-pill">
                                    {filteredProduits.length} produits
                                </Badge>
                            </div>
                        </div>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div className="table-responsive">
                            <Table hover className="align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th className="ps-4">Référence</th>
                                        <th style={{ minWidth: '300px' }}>Produit</th>
                                        <th>Emplacement</th>
                                        <th className="text-center">Stock théorique</th>
                                        <th className="text-center">Stock réel</th>
                                        <th className="text-center pe-4">Écart</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentItems.length > 0 ? (
                                        currentItems.map(p => {
                                            const quantiteSaisie = getQuantiteSaisie(p.id_produit);
                                            const ecart = quantiteSaisie ? quantiteSaisie - (p.stock_actuel || 0) : 0;
                                            return (
                                                <tr key={p.id_produit}>
                                                    <td className="ps-4">
                                                        <span className="reference-badge">{p.reference}</span>
                                                    </td>
                                                    <td>
                                                        <div className="fw-semibold">{p.designation}</div>
                                                        <small className="text-muted">{p.nom_categorie || '-'}</small>
                                                    </td>
                                                    <td>{p.emplacement || '-'}</td>
                                                    <td className="text-center fw-bold">{p.stock_actuel || 0}</td>
                                                    <td className="text-center" style={{ minWidth: '120px' }}>
                                                        <Form.Control
                                                            type="number"
                                                            min="0"
                                                            value={quantiteSaisie}
                                                            onChange={(e) => handleQuantiteChange(p.id_produit, e.target.value)}
                                                            className="text-center border-2 rounded-3"
                                                            style={{ width: '100px', margin: '0 auto' }}
                                                        />
                                                    </td>
                                                    <td className="text-center pe-4">
                                                        {quantiteSaisie !== '' && (
                                                            <span className={`fw-bold ${ecart > 0 ? 'text-success' : ecart < 0 ? 'text-danger' : ''}`}>
                                                                {ecart > 0 ? '+' : ''}{ecart}
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="text-center py-5">
                                                <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                                                <h5>Aucun produit trouvé</h5>
                                                <p className="text-muted">Ajustez vos filtres de recherche</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                <div className="text-muted small">
                                    Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredProduits.length)} sur {filteredProduits.length} produits
                                </div>
                                <Pagination className="mb-0">
                                    <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                    {renderPagination()}
                                    <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                </Pagination>
                            </div>
                        )}
                    </Card.Body>
                </Card>

                {/* Bouton flottant pour valider */}
                {inventaire.lignes.length > 0 && (
                    <div style={{ position: 'fixed', bottom: '30px', right: '30px', zIndex: 1000 }}>
                        <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            className="rounded-pill shadow-lg px-4"
                            disabled={loading}
                            style={{ background: COLORS.primary, border: 'none' }}
                        >
                            {loading ? (
                                <Spinner size="sm" />
                            ) : (
                                <>
                                    <i className="ri-check-double-line me-2"></i>
                                    Valider l'inventaire ({inventaire.lignes.length} produits)
                                </>
                            )}
                        </Button>
                    </div>
                )}
            </Form>

            <style>{`
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: ${COLORS.primary};
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }

                .reference-badge {
                    background: #f1f5f9;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                    display: inline-block;
                }

                .rounded-3 {
                    border-radius: 12px !important;
                }
                .rounded-pill {
                    border-radius: 50px !important;
                }

                /* Pagination (identique aux autres pages) */
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: ${COLORS.primary};
                    border-color: ${COLORS.primary};
                    color: white;
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }

                .fs-48 {
                    font-size: 3rem;
                }
                .text-white-50 {
                    color: rgba(255,255,255,0.5);
                }
            `}</style>
        </>
    );
}